import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePlatform, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';
import {
  getCompleteAd3Url,
  getPlayContinueUrl,
  getPlayEndUrl,
  getPlayItemReuseUrl,
  getPlayStart2Url,
  getRecentPlayHistoryUrl,
  getRoundResetUrl,
  getStartAd3Url,
  getState2Url,
} from '../../api/generated/cat-runner/game-cat-runner/game-cat-runner.ts';
import CatRunningGameScreen from './components/CatRunningGameScreen';
import CatRunningLobbyScreen from './components/CatRunningLobbyScreen';
import CatRunningReadyScreen from './components/CatRunningReadyScreen';
import PunchInfoBottomSheet from '../punch/PunchInfoBottomSheet';
import { CAT_RUNNER_PRE_ITEM_TYPES } from './mockBackend';
import { createInitialItemUseCounts, itemUsageToUseCounts } from './game/itemPricing';
import { saveCatRunningReplay } from './replayStorage';
import magnetIcon from '../../assets/cat-running/items/magnet.png';
import shieldIcon from '../../assets/cat-running/items/shield.png';
import heartCounterIcon from '../../assets/cat-running/ui/icon-heart.png';
import snackCounterIcon from '../../assets/cat-running/ui/icon-snack-counter.png';

const CAT_RUNNING_ACTIVE_ROUND_KEY = 'catRunning:activeRound';
const CAT_RUNNER_ERROR_CODE = {
  ROUND_INACTIVE: 'ROUND_INACTIVE',
};

const loadCatRunningActiveRound = () => {
  try {
    const raw = window.sessionStorage.getItem(CAT_RUNNING_ACTIVE_ROUND_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Failed to load cat running active round', error);
    return null;
  }
};

const saveCatRunningActiveRound = (round) => {
  try {
    if (round?.roundToken) {
      window.sessionStorage.setItem(CAT_RUNNING_ACTIVE_ROUND_KEY, JSON.stringify(round));
    } else {
      window.sessionStorage.removeItem(CAT_RUNNING_ACTIVE_ROUND_KEY);
    }
  } catch (error) {
    console.error('Failed to save cat running active round', error);
  }
};

const CAT_RUNNING_ITEM_TOAST_MESSAGE = {
  [CAT_RUNNER_PRE_ITEM_TYPES.MAGNET]: '자석 아이템을 획득했어요!',
  [CAT_RUNNER_PRE_ITEM_TYPES.SHIELD]: '무적방패 아이템을 획득했어요!',
};

const CAT_RUNNING_ITEM_ACTIVATION_MESSAGE = {
  [CAT_RUNNER_PRE_ITEM_TYPES.MAGNET]: { icon: magnetIcon, label: '자석 활성!' },
  [CAT_RUNNER_PRE_ITEM_TYPES.SHIELD]: { icon: shieldIcon, label: '방패 활성!' },
};

const getResourceUseToastMessage = ({ icon, label, cost = 1, suffix } = {}) => (
  <span className="inline-flex items-center justify-center gap-[18px]">
    <span className="inline-flex items-center gap-1.5">
      <img src={icon} alt="" className="h-5 w-5 object-contain" />
      <span>{`${label} ${cost}개 ${label === '무료하트' ? '사용' : '소비'}!`}</span>
    </span>
    {suffix ? (
      <>
        <span>/</span>
        <span className="inline-flex items-center gap-1.5">
          <img src={suffix.icon} alt="" className="h-5 w-5 object-contain" />
          <span>{suffix.label}</span>
        </span>
      </>
    ) : null}
  </span>
);

const getSnackUseToastMessage = (cost = 1, suffix) =>
  getResourceUseToastMessage({ icon: snackCounterIcon, label: '간식', cost, suffix });

const getSnackGainToastMessage = (amount) => (
  <span className="inline-flex items-center justify-center gap-[18px]">
    <img src={snackCounterIcon} alt="" className="h-5 w-5 object-contain" />
    <span>{`간식 +${amount}개 획득!`}</span>
  </span>
);

const createRuntimeStateSnapshot = (payload) => ({
  balance: payload?.balance ?? 0,
  snackCount: payload?.snackCount ?? 0,
  heartsRemaining: payload?.heartsRemaining ?? 0,
  randomRefreshAvailable: payload?.randomRefreshAvailable ?? false,
  randomRefreshFreeAvailable: payload?.randomRefreshFreeAvailable ?? false,
  randomRefreshAdRequired: payload?.randomRefreshAdRequired ?? false,
  state: payload?.state ?? {},
  activeRound: payload?.activeRound ?? null,
});

const getArmedPreItemTypesFromState = (userState) => {
  const armed = [];
  if (Number(userState?.preItemMagnetArmed ?? 0) === 1) armed.push(CAT_RUNNER_PRE_ITEM_TYPES.MAGNET);
  if (Number(userState?.preItemShieldArmed ?? 0) === 1) armed.push(CAT_RUNNER_PRE_ITEM_TYPES.SHIELD);
  return armed;
};

const createClientItemUseCounts = (itemUsage) =>
  itemUsage ? itemUsageToUseCounts(itemUsage) : createInitialItemUseCounts();

export default function CatRunningPage() {
  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  const location = useLocation();
  const navigate = useNavigate();
  const isLobbyScreen = location.pathname === '/cat-running' || location.pathname === '/cat-running/';
  const browserDeviceType = usePlatform();
  const { showToast, showErrorToast } = useToast();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const [runtimeState, setRuntimeState] = useState(null);
  const [activeRound, setActiveRound] = useState(loadCatRunningActiveRound);
  const [isRoundResetting, setIsRoundResetting] = useState(false);
  const [isRoundResetReady, setIsRoundResetReady] = useState(false);
  const [isPendingPreItemAd, setIsPendingPreItemAd] = useState(false);
  const [adLoadingItemType, setAdLoadingItemType] = useState(null);
  const [snackShortagePopup, setSnackShortagePopup] = useState(false);
  const [isPendingSnackChargeAd, setIsPendingSnackChargeAd] = useState(false);
  const [isPendingContinueAd, setIsPendingContinueAd] = useState(false);
  const [isGuideSheetOpen, setIsGuideSheetOpen] = useState(false);
  const pendingPreItemTypeRef = useRef(null);
  const pendingPreItemAdTokenRef = useRef(null);
  const pendingSnackChargeAdTokenRef = useRef(null);
  const pendingContinueAdTokenRef = useRef(null);
  const pendingContinueDeathRef = useRef(null);
  const continueAdResolverRef = useRef(null);
  const playEntryAuthorizedRef = useRef(false);

  const fetcher = useCallback(async (...args) => fetchWithToken(...args), [fetchWithToken]);
  const { data: catRunnerStateResponse, mutate: mutateCatRunnerState } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getState2Url()}`, accessToken] : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );
  const { data: recentPlayHistoryResponse, mutate: mutateRecentPlayHistory } = useSWR(
    accessToken && isLobbyScreen
      ? [`${API_SERVER_DOMAIN}${getRecentPlayHistoryUrl({ limit: 10 })}`, accessToken]
      : null,
    fetcher,
    { dedupingInterval: 0, revalidateOnMount: true },
  );

  useEffect(() => {
    if (!catRunnerStateResponse) return;
    setRuntimeState(createRuntimeStateSnapshot(catRunnerStateResponse));
  }, [catRunnerStateResponse]);

  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);

  const currentState = runtimeState ?? (catRunnerStateResponse ? createRuntimeStateSnapshot(catRunnerStateResponse) : null);
  const snackCount = currentState?.snackCount ?? 0;
  const heartsRemaining = currentState?.heartsRemaining ?? 0;
  const armedPreItemTypes = useMemo(() => getArmedPreItemTypesFromState(currentState?.state), [currentState?.state]);

  const syncRuntimeStateFromPayload = useCallback((payload) => {
    setRuntimeState((prev) => ({
      ...(prev ?? {}),
      ...createRuntimeStateSnapshot(payload),
    }));
  }, []);

  const leaveInactiveRound = useCallback(
    (message) => {
      showErrorToast(message || '이미 종료된 라운드입니다.');
      playEntryAuthorizedRef.current = false;
      setActiveRound(null);
      navigate('/cat-running', { replace: true });
    },
    [navigate, showErrorToast],
  );

  const handleRoundApiFailure = useCallback(
    (payload, fallbackMessage) => {
      const message = payload?.message || fallbackMessage;
      if (payload?.code === CAT_RUNNER_ERROR_CODE.ROUND_INACTIVE) {
        leaveInactiveRound(message);
        return true;
      }
      showErrorToast(message);
      return false;
    },
    [leaveInactiveRound, showErrorToast],
  );

  useEffect(() => {
    saveCatRunningActiveRound(activeRound);
  }, [activeRound]);

  const handlePreItemAdError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingPreItemAd) return;
      pendingPreItemTypeRef.current = null;
      pendingPreItemAdTokenRef.current = null;
      setIsPendingPreItemAd(false);
      setAdLoadingItemType(null);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
      mutateCatRunnerState();
    },
    [isPendingPreItemAd, mutateCatRunnerState, showErrorToast],
  );

  useAdsListener('', '', { task: 'cat-runner-ready-pre-item', pending: isPendingPreItemAd, onError: handlePreItemAdError }, async () => {
    if (!isPendingPreItemAd || !accessToken) return;

    const itemType = pendingPreItemTypeRef.current;
    const adToken = pendingPreItemAdTokenRef.current;
    pendingPreItemTypeRef.current = null;
    pendingPreItemAdTokenRef.current = null;
    setIsPendingPreItemAd(false);

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placement: 'preItem',
          itemType,
          adToken,
        }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '광고 보상 처리에 실패했어요.');
        mutateCatRunnerState();
        return;
      }

      syncRuntimeStateFromPayload(payload);
      // revalidate 없이 캐시 갱신: 동시 getState가 장착 플래그를 되돌리는 레이스를 줄인다.
      mutateCatRunnerState(payload, { revalidate: false });
      const toastMessage = CAT_RUNNING_ITEM_TOAST_MESSAGE[itemType] || '아이템을 획득했어요!';
      showToast(toastMessage);
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/complete', { error });
      showErrorToast('광고 보상 처리에 실패했어요.');
      mutateCatRunnerState();
    } finally {
      setAdLoadingItemType(null);
    }
  });

  const handleSnackChargeAdError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingSnackChargeAd) return;
      pendingSnackChargeAdTokenRef.current = null;
      setIsPendingSnackChargeAd(false);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
    },
    [isPendingSnackChargeAd, showErrorToast],
  );

  useAdsListener('', '', { task: 'cat-runner-ready-snack-charge', pending: isPendingSnackChargeAd, onError: handleSnackChargeAdError }, async () => {
    if (!isPendingSnackChargeAd || !accessToken) return;

    const adToken = pendingSnackChargeAdTokenRef.current;
    pendingSnackChargeAdTokenRef.current = null;
    setIsPendingSnackChargeAd(false);

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placement: 'snackCharge', adToken }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '광고 보상 처리에 실패했어요.');
        mutateCatRunnerState();
        return;
      }

      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      const grantedSnack = Number(payload?.grantedSnack ?? 0);
      if (grantedSnack > 0) {
        setSnackShortagePopup(false);
        const toastMessage = getSnackGainToastMessage(grantedSnack);
        showToast(toastMessage);
      }
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/complete (snackCharge)', { error });
      showErrorToast('광고 보상 처리에 실패했어요.');
      mutateCatRunnerState();
    }
  });

  const handleContinueAdError = useCallback(
    (errorInfo = {}) => {
      if (!isPendingContinueAd) return;
      pendingContinueAdTokenRef.current = null;
      pendingContinueDeathRef.current = null;
      setIsPendingContinueAd(false);
      const resolve = continueAdResolverRef.current;
      continueAdResolverRef.current = null;
      resolve?.(false);
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      showErrorToast(message);
    },
    [isPendingContinueAd, showErrorToast],
  );

  const applyContinueSuccess = useCallback(
    (payload, { paidWithSnack = true, previousSnackCount = snackCount } = {}) => {
      syncRuntimeStateFromPayload(payload);
      setActiveRound((prev) => ({
        ...(prev ?? {}),
        roundToken: payload?.roundToken ?? prev?.roundToken,
        preItems: payload?.preItems ?? prev?.preItems ?? [],
        itemUsage: payload?.itemUsage ?? prev?.itemUsage ?? {},
        clientItemUseCounts: createClientItemUseCounts(payload?.itemUsage ?? prev?.itemUsage),
        warmupUntilM: payload?.warmupUntilM ?? prev?.warmupUntilM,
        continueRemaining: payload?.continueRemaining ?? prev?.continueRemaining,
        countdownRequired: payload?.countdownRequired ?? prev?.countdownRequired,
      }));
      mutateCatRunnerState();
      if (paidWithSnack) {
        const usedSnacks = Math.max(0, previousSnackCount - Number(payload?.snackCount ?? previousSnackCount));
        showToast(getSnackUseToastMessage(usedSnacks || 1));
      } else {
        showToast('광고 시청으로 이어하기!');
      }
    },
    [mutateCatRunnerState, showToast, snackCount, syncRuntimeStateFromPayload],
  );

  useAdsListener('', '', { task: 'cat-runner-continue', pending: isPendingContinueAd, onError: handleContinueAdError }, async () => {
    if (!isPendingContinueAd || !accessToken) return;

    const adToken = pendingContinueAdTokenRef.current;
    const deathInfo = pendingContinueDeathRef.current;
    const resolve = continueAdResolverRef.current;
    pendingContinueAdTokenRef.current = null;
    pendingContinueDeathRef.current = null;
    continueAdResolverRef.current = null;
    setIsPendingContinueAd(false);

    if (!adToken || !deathInfo?.roundToken) {
      showErrorToast('이어하기 정보를 찾을 수 없어요.');
      resolve?.(false);
      return;
    }

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getCompleteAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placement: 'continue',
          adToken,
          roundToken: deathInfo.roundToken,
          deathDistanceM: deathInfo.deathDistanceM,
          deathChuruCount: deathInfo.deathChuruCount,
        }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        handleRoundApiFailure(payload, '이어하기에 실패했어요.');
        mutateCatRunnerState();
        resolve?.(false);
        return;
      }

      applyContinueSuccess(payload, { paidWithSnack: false });
      resolve?.(true);
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/complete (continue)', { error });
      showErrorToast('이어하기에 실패했어요.');
      mutateCatRunnerState();
      resolve?.(false);
    }
  });

  const handleArmPreItem = useCallback(
    async (itemType) => {
      if (!itemType) return;
      if (!accessToken) {
        showErrorToast('로그인 후 이용해 주세요.');
        return;
      }
      if (armedPreItemTypes.includes(itemType) || isPendingPreItemAd || adLoadingItemType) return;

      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        showErrorToast('앱 광고 실행 핸들러를 찾지 못했어요.');
        return;
      }

      try {
        setAdLoadingItemType(itemType);
        const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            placement: 'preItem',
            itemType,
            deviceType,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          setAdLoadingItemType(null);
          showErrorToast(payload?.message || '광고를 불러오지 못했어요.');
          return;
        }

        pendingPreItemTypeRef.current = itemType;
        pendingPreItemAdTokenRef.current = payload?.adToken ?? null;
        setIsPendingPreItemAd(true);
        runAdmob(payload?.adType ?? '', payload?.placementId ?? '');
      } catch (error) {
        console.error('backend api: error POST /cat-runner/ads/start', { error });
        setAdLoadingItemType(null);
        showErrorToast('광고를 불러오지 못했어요.');
      }
    },
    [
      API_SERVER_DOMAIN,
      accessToken,
      adLoadingItemType,
      armedPreItemTypes,
      deviceType,
      isPendingPreItemAd,
      showErrorToast,
    ],
  );

  const handleSnackCharge = useCallback(async () => {
    if (isPendingSnackChargeAd) return;
    if (!accessToken) {
      showErrorToast('로그인 후 이용해 주세요.');
      return;
    }
    if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
      showErrorToast('광고 재생 모듈을 찾을 수 없어요.');
      return;
    }

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ placement: 'snackCharge', deviceType }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.result === 'fail' || !payload?.adToken) {
        showErrorToast(payload?.message || '광고를 불러오지 못했어요.');
        return;
      }

      pendingSnackChargeAdTokenRef.current = payload.adToken;
      setIsPendingSnackChargeAd(true);
      runAdmob(payload?.adType ?? '', payload?.placementId ?? '');
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/start (snackCharge)', { error });
      showErrorToast('광고를 불러오지 못했어요.');
    }
  }, [API_SERVER_DOMAIN, accessToken, deviceType, isPendingSnackChargeAd, showErrorToast]);

  const handleContinueByAd = useCallback(
    async ({ deathDistanceM, deathChuruCount } = {}) => {
      if (isPendingContinueAd) return false;
      if (!accessToken || !activeRound?.roundToken) {
        showErrorToast('이어하기 정보를 찾을 수 없어요.');
        return false;
      }
      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        showErrorToast('앱 광고 실행 핸들러를 찾지 못했어요.');
        return false;
      }

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placement: 'continue', deviceType }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail' || !payload?.adToken) {
          handleRoundApiFailure(payload, '광고를 불러오지 못했어요.');
          return false;
        }

        pendingContinueAdTokenRef.current = payload.adToken;
        pendingContinueDeathRef.current = {
          roundToken: activeRound.roundToken,
          deathDistanceM,
          deathChuruCount,
        };

        return new Promise((resolve) => {
          continueAdResolverRef.current = resolve;
          setIsPendingContinueAd(true);
          runAdmob(payload?.adType ?? '', payload?.placementId ?? '');
        });
      } catch (error) {
        console.error('backend api: error POST /cat-runner/ads/start (continue)', { error });
        showErrorToast('광고를 불러오지 못했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, activeRound?.roundToken, deviceType, handleRoundApiFailure, isPendingContinueAd, showErrorToast],
  );

  const handleStartGame = useCallback(async () => {
    if (!accessToken) {
      showErrorToast('로그인 후 이용해 주세요.');
      return;
    }
    if (heartsRemaining <= 0 && snackCount <= 0) {
      setSnackShortagePopup(true);
      return;
    }

    // BGM unlock/play는 ReadyScreen 시작 버튼 pointerdown에서 처리한다.
    // (여기서는 API await 이후라 모바일 제스처가 이미 끊긴 상태)

    const previousHeartsRemaining = heartsRemaining;
    const previousSnackCount = snackCount;

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getPlayStart2Url()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mode: 'SINGLE',
          preItems: armedPreItemTypes,
        }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail' || !payload?.roundToken) {
        showErrorToast(payload?.message || '캣러닝 시작에 실패했어요.');
        return;
      }

      const activePreItems = payload.preItems ?? armedPreItemTypes;
      setActiveRound({
        roundToken: payload.roundToken,
        mode: payload.mode ?? 'SINGLE',
        preItems: activePreItems,
        itemUsage: payload.itemUsage ?? {},
        clientItemUseCounts: createClientItemUseCounts(payload.itemUsage),
        runSeed: payload.runSeed,
        spawnManifest: payload.spawnManifest,
        warmupUntilM: payload.warmupUntilM,
        continueRemaining: payload.continueRemaining,
        startedAt: payload.startedAt,
        expiresAt: payload.expiresAt,
        countdownRequired: payload.countdownRequired,
      });
      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      const usedHearts = Math.max(0, previousHeartsRemaining - Number(payload?.heartsRemaining ?? previousHeartsRemaining));
      const usedSnacks = Math.max(0, previousSnackCount - Number(payload?.snackCount ?? previousSnackCount));
      if (usedHearts > 0) {
        showToast(getResourceUseToastMessage({ icon: heartCounterIcon, label: '무료하트', cost: usedHearts }));
      } else {
        showToast(getSnackUseToastMessage(usedSnacks || 1));
      }
      playEntryAuthorizedRef.current = true;
      navigate('/cat-running/play');
    } catch (error) {
      console.error('backend api: error POST /cat-runner/play/start', { error });
      showErrorToast('캣러닝 시작에 실패했어요.');
    }
  }, [
    API_SERVER_DOMAIN,
    accessToken,
    armedPreItemTypes,
    heartsRemaining,
    mutateCatRunnerState,
    navigate,
    snackCount,
    showErrorToast,
    showToast,
    syncRuntimeStateFromPayload,
  ]);

  const handleContinueRound = useCallback(
    async ({ deathDistanceM, deathChuruCount } = {}) => {
      if (!accessToken || !activeRound?.roundToken) {
        showErrorToast('이어하기 정보를 찾을 수 없어요.');
        return false;
      }

      const previousSnackCount = snackCount;

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayContinueUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roundToken: activeRound.roundToken,
            deathDistanceM,
            deathChuruCount,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          handleRoundApiFailure(payload, '이어하기에 실패했어요.');
          return false;
        }

        applyContinueSuccess(payload, { paidWithSnack: true, previousSnackCount });
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/continue', { error });
        showErrorToast('이어하기에 실패했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, activeRound?.roundToken, applyContinueSuccess, handleRoundApiFailure, showErrorToast, snackCount],
  );

  const handleFinishRound = useCallback(
    async (replayPayload, { preserveActiveRound = false } = {}) => {
      if (!replayPayload?.playEndRequest || !accessToken) {
        showErrorToast('게임 종료 정보를 찾을 수 없어요.');
        return false;
      }

      saveCatRunningReplay(replayPayload);

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayEndUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(replayPayload.playEndRequest),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          handleRoundApiFailure(payload, '게임 결과 정산에 실패했어요.');
          return false;
        }

        syncRuntimeStateFromPayload(payload);
        mutateCatRunnerState();
        if (isLobbyScreen) mutateRecentPlayHistory();
        if (!preserveActiveRound) setActiveRound(null);
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/end', { error });
        showErrorToast('게임 결과 정산에 실패했어요.');
        return false;
      }
    },
    [
      API_SERVER_DOMAIN,
      accessToken,
      handleRoundApiFailure,
      isLobbyScreen,
      mutateCatRunnerState,
      mutateRecentPlayHistory,
      showErrorToast,
      syncRuntimeStateFromPayload,
    ],
  );

  const handleUseItem = useCallback(
    async (itemType) => {
      if (!accessToken || !activeRound?.roundToken) {
        showErrorToast('아이템 사용 정보를 찾을 수 없어요.');
        return false;
      }

      const previousSnackCount = snackCount;

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayItemReuseUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roundToken: activeRound.roundToken,
            itemType,
          }),
        });
        const payload = await response.json().catch(() => null);

        if (!response.ok || payload?.result === 'fail') {
          handleRoundApiFailure(payload, '아이템 사용에 실패했어요.');
          return false;
        }

        syncRuntimeStateFromPayload(payload);
        setActiveRound((prev) => ({
          ...(prev ?? {}),
          itemUsage: payload?.itemUsage ?? prev?.itemUsage ?? {},
          clientItemUseCounts: createClientItemUseCounts(payload?.itemUsage ?? prev?.itemUsage),
        }));
        mutateCatRunnerState();
        const usedSnacks = Math.max(
          0,
          previousSnackCount - Number(payload?.snackCount ?? previousSnackCount),
        );
        const snackCost = Number(payload?.snackCost) || usedSnacks;
        if (snackCost > 0) {
          const activationMessage = CAT_RUNNING_ITEM_ACTIVATION_MESSAGE[payload?.itemType ?? itemType];
          showToast(getSnackUseToastMessage(snackCost, activationMessage));
        }
        return true;
      } catch (error) {
        console.error('backend api: error POST /cat-runner/play/item/reuse', { error });
        showErrorToast('아이템 사용에 실패했어요.');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, activeRound?.roundToken, handleRoundApiFailure, mutateCatRunnerState, showErrorToast, showToast, snackCount, syncRuntimeStateFromPayload],
  );

  const screen = (() => {
    if (location.pathname === '/cat-running/play') {
      return 'game';
    }

    if (location.pathname === '/cat-running/ready') {
      return 'ready';
    }

    return 'lobby';
  })();

  const resetRoundOnReadyEntry = useCallback(async () => {
    if (!accessToken) return false;

    setIsRoundResetting(true);

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}${getRoundResetUrl()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '게임 준비 상태를 초기화하지 못했어요.');
        return false;
      }

      setActiveRound(null);
      syncRuntimeStateFromPayload(payload);
      mutateCatRunnerState();
      return true;
    } catch (error) {
      console.error('backend api: error POST /cat-runner/round/reset', { error });
      showErrorToast('게임 준비 상태를 초기화하지 못했어요.');
      return false;
    } finally {
      setIsRoundResetting(false);
    }
  }, [
    API_SERVER_DOMAIN,
    accessToken,
    mutateCatRunnerState,
    showErrorToast,
    syncRuntimeStateFromPayload,
  ]);

  const resetRoundOnReadyEntryRef = useRef(resetRoundOnReadyEntry);
  resetRoundOnReadyEntryRef.current = resetRoundOnReadyEntry;

  useEffect(() => {
    if (screen !== 'ready' || !accessToken) {
      setIsRoundResetReady(false);
      return undefined;
    }

    let isActive = true;
    setIsRoundResetReady(false);

    // resetRoundOnReadyEntry 참조 변경으로 재호출되면 장착 상태가 꼬일 수 있어
    // ready 화면 진입(screen/accessToken) 시에만 1회 실행한다.
    resetRoundOnReadyEntryRef.current().then((isResetReady) => {
      if (isActive) setIsRoundResetReady(isResetReady);
    });

    return () => {
      isActive = false;
    };
  }, [accessToken, screen]);

  const hasStoredActiveRound = Boolean(activeRound?.roundToken);
  const canEnterGame = hasStoredActiveRound && !isRoundResetting && playEntryAuthorizedRef.current;

  useEffect(() => {
    if (screen === 'game') return;

    playEntryAuthorizedRef.current = false;
  }, [screen]);

  useEffect(() => {
    if (screen !== 'game' || canEnterGame) return undefined;

    playEntryAuthorizedRef.current = false;
    navigate(hasStoredActiveRound ? '/cat-running/ready' : '/cat-running', { replace: true });
    return undefined;
  }, [canEnterGame, hasStoredActiveRound, navigate, screen]);

  const catRunningGuideSheet = (
    <PunchInfoBottomSheet
      isOpen={isGuideSheetOpen}
      onClose={() => setIsGuideSheetOpen(false)}
      title="캣러닝 이용안내"
      sections={[
        {
          bullet: false,
          items: ['캣러닝 게임 도중 이탈 시 라운드는 자동 포기되며, 보상은 지급되지 않습니다.'],
        },
      ]}
      sectionsClassName="font-['Pretendard'] text-[16px] font-medium leading-[1.6] tracking-normal text-gray05"
    />
  );
  if (screen === 'game') {
    if (!canEnterGame) return null;

    return (
      <CatRunningGameScreen
        onBack={() => {
          playEntryAuthorizedRef.current = false;
          setActiveRound(null);
          navigate('/cat-running', { replace: true });
        }}
        onContinueRound={handleContinueRound}
        onContinueByAd={handleContinueByAd}
        onFinishRound={handleFinishRound}
        onUseItem={handleUseItem}
        onItemUseCountChange={(itemType, count) => {
          setActiveRound((prev) => ({
            ...(prev ?? {}),
            clientItemUseCounts: {
              ...(prev?.clientItemUseCounts ?? {}),
              [itemType]: count,
            },
          }));
        }}
        snackCount={snackCount}
        activeRound={activeRound}
        activePreItemTypes={activeRound?.preItems ?? armedPreItemTypes}
        itemUseCounts={activeRound?.clientItemUseCounts}
        previewGameOver={location.search.includes('preview=gameover')}
        continueAdLoading={isPendingContinueAd}
      />
    );
  }

  if (screen === 'ready') {
    return (
      <>
        <CatRunningReadyScreen
          onBack={() => navigate('/cat-running')}
          onHome={() => navigate('/cat-running')}
          onStart={handleStartGame}
          onOpenGuide={() => setIsGuideSheetOpen(true)}
          armedPreItemTypes={armedPreItemTypes}
          onArmPreItem={handleArmPreItem}
          isRoundResetting={isRoundResetting}
          isRoundResetReady={isRoundResetReady}
          snackCount={snackCount}
          heartsRemaining={heartsRemaining}
          adLoadingItemType={adLoadingItemType}
          snackShortageOpen={snackShortagePopup}
          snackChargeLoading={isPendingSnackChargeAd}
          onCloseSnackShortage={() => setSnackShortagePopup(false)}
          onChargeSnack={handleSnackCharge}
        />
        {catRunningGuideSheet}
      </>
    );
  }

  return (
    <>
      <CatRunningLobbyScreen
        onBattle={() => navigate('/cat-running/battle')}
        onStart={() => navigate('/cat-running/ready')}
        onReplayTest={() => navigate('/cat-running/replay-test')}
        onRanking={() => navigate('/cat-running/ranking')}
        onOpenGuide={() => setIsGuideSheetOpen(true)}
        snackCount={snackCount}
        playHistory={recentPlayHistoryResponse?.list ?? []}
      />
      {catRunningGuideSheet}
    </>
  );
}
